import type { Schema } from "./api";

type RecordValue = Record<string, unknown>;
const record = (value: unknown): RecordValue | null =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? value as RecordValue : null;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const checksum = /^[0-9a-f]{64}$/i;
const count = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const questionKeys = ["schema_version", "path", "sha256", "operator_checks_path", "operator_checks_sha256",
  "historical_questions_sha256", "historical_audits_sha256", "checked_against_input_hash",
  "historical_questions", "resolved_original_questions", "external_original_questions",
  "needs_internal_work", "unresolved_questions"];

function currentQuestions(delivery: RecordValue, verified: RecordValue): boolean {
  if (!count(delivery.unresolved_questions)) return false;
  const questions = record(delivery.question_register), other = record(verified.question_register);
  // A historical delivery has no claim that all specialist questions were resolved.
  if (!questions && !other) return delivery.unresolved_questions > 0;
  if (!questions || !other || questionKeys.some(key => questions[key] !== other[key])
      || questions.schema_version !== 1 || questions.needs_internal_work !== 0
      || questions.unresolved_questions !== delivery.unresolved_questions
      || questions.checked_against_input_hash !== delivery.post_repair_input_hash
      || verified.post_repair_input_hash !== delivery.post_repair_input_hash) return false;
  if (["sha256", "operator_checks_sha256", "historical_questions_sha256", "historical_audits_sha256",
    "checked_against_input_hash"].some(key => typeof questions[key] !== "string"
      || !checksum.test(questions[key] as string))) return false;
  const historical = questions.historical_questions, resolved = questions.resolved_original_questions,
    external = questions.external_original_questions;
  return count(historical) && historical > 0 && count(resolved) && count(external)
    && resolved + external === historical
    && (external === 0 ? delivery.unresolved_questions === 0 : delivery.unresolved_questions > 0);
}

export type TargetedDelivery = { href: string; corrected: number; files: number; questions: number;
  supplements?: {href: string; label: string}[] };

/** A stopped full-review job may have a separately accepted, immutable delivery. */
export function targetedDelivery(job: Pick<Schema["JobOut"], "kind" | "details">): TargetedDelivery | null {
  const report = record(job.details.final_review), closeout = record(job.details.targeted_closeout);
  if (job.kind !== "final_review" || report?.phase !== "delivered_for_professional_review"
      || closeout?.status !== "verified" || closeout.phase !== "delivered"
      || report.full_review_passed !== false || report.full_reaudit_after_last_repairs !== false
      || report.human_review_required !== true || report.targeted_completed_findings !== 18) return null;
  const delivery = record(report.delivery), verified = record(closeout.delivery);
  if (!delivery || !verified || delivery.status !== "technically_accepted_for_professional_review"
      || delivery.professional_approval !== false || delivery.full_reaudit_after_repairs !== false
      || !count(delivery.files_verified) || delivery.files_verified === 0
      || !currentQuestions(delivery, verified)
      || typeof delivery.snapshot_id !== "string" || !uuid.test(delivery.snapshot_id)
      || ["archive_sha256", "download_proof_sha256", "acceptance_receipt_sha256"].some(key =>
        typeof delivery[key] !== "string" || !checksum.test(delivery[key] as string))) return null;
  const keys = ["status", "snapshot_id", "archive_sha256", "download_proof_sha256", "acceptance_receipt_sha256",
    "files_verified", "unresolved_questions", "professional_approval", "full_reaudit_after_repairs", "download_url"];
  if (keys.some(key => delivery[key] !== verified[key])) return null;
  const href = `/api/v1/exports/${delivery.snapshot_id}/download`;
  if (typeof delivery.download_url !== "string") return null;
  try {
    const given = new URL(delivery.download_url, "https://workbench.invalid");
    if (!["http:", "https:"].includes(given.protocol) || given.pathname !== href
        || given.search || given.hash || given.username || given.password) return null;
  } catch { return null; }
  // Never render the stored URL: the local authenticated endpoint is derived from a UUID.
  const presentation=record(delivery.presentation), otherPresentation=record(verified.presentation);
  let supplements: {href: string; label: string}[]=[];
  if(presentation && JSON.stringify(presentation)===JSON.stringify(otherPresentation)
      && presentation.saved_quantities_changed===false && Array.isArray(presentation.downloads)) {
    const labels:Record<string,string>={docx:'Расчёты и исходники · Word',xlsx:'Расчёты и исходники · Excel',pdf:'Расчёты и исходники · PDF'};
    const entries=presentation.downloads.map(record);
    if(entries.length===3 && new Set(entries.map(row=>row?.format)).size===3 && entries.every(row=>
      row && typeof row.snapshot_id==='string' && uuid.test(row.snapshot_id)
      && typeof row.sha256==='string' && checksum.test(row.sha256) && typeof row.format==='string' && Object.hasOwn(labels,row.format))) {
      supplements=entries.map(row=>({href:`/api/v1/exports/${row!.snapshot_id}/download`,label:labels[row!.format as string]}));
    }
  }
  return { href, corrected: report.targeted_completed_findings, files: delivery.files_verified,
    questions: delivery.unresolved_questions as number,...(supplements.length?{supplements}:{}) };
}
